import { memo, type ReactNode } from 'react'
import { AgentMessage, AgentToolCall, MessageRole } from '@multiplayer-app/ai-agent-types'
import { useAgentRuntime } from '@/context/AgentProvider'
import { ToolCallCard } from './ToolCallCard'
import { MessageAvatar, MessageBubble, MessageHeader, ReasoningPanel, MessageTypingAnimation } from './Message'

const dedupeToolCalls = (calls: AgentToolCall[]): AgentToolCall[] => {
  if (calls.length <= 1) return calls
  const byId = new Map<string, AgentToolCall>()
  for (const call of calls) {
    byId.set(call.id, call)
  }
  return Array.from(byId.values())
}

/**
 * Ordered render: walk `message.parts` so reasoning/text/tool-call blocks appear
 * in the exact order the model produced them (e.g. reasoning → text → reasoning →
 * tool-call → text), instead of collapsing each kind into a single bucket.
 */
const OrderedMessageParts = ({
  message,
  contextKey,
  isStreaming,
  reasoningEnabled
}: {
  message: AgentMessage
  contextKey: string
  isStreaming?: boolean
  reasoningEnabled: boolean
}) => {
  const toolCallsById = new Map((message.toolCalls ?? []).map((call) => [call.id, call]))

  return (
    <>
      {(message.parts ?? []).map((part, index) => {
        if (part.type === 'reasoning') {
          if (!reasoningEnabled || !part.text.trim()) return null
          return <ReasoningPanel key={index} reasoning={part.text} isStreaming={isStreaming} />
        }
        if (part.type === 'text') {
          if (!part.text.trim()) return null
          // Reuse MessageBubble for a single text segment.
          return (
            <MessageBubble
              key={index}
              message={{ ...message, content: part.text, reasoning: undefined, toolCalls: [] }}
            />
          )
        }
        // tool-call — render the card directly; it returns null when there's
        // nothing to show, so no wrapper is emitted for empty tool calls.
        const call = toolCallsById.get(part.toolCallId)
        if (!call) return null
        return (
          <ToolCallCard
            key={index}
            call={call}
            contextKey={contextKey}
            messageId={message.id}
            chatId={message.chat}
          />
        )
      })}
      {isStreaming && (message.parts?.length ?? 0) === 0 && <MessageTypingAnimation />}
    </>
  )
}

const LegacyMessageParts = ({
  message,
  contextKey,
  isStreaming,
  reasoningEnabled
}: {
  message: AgentMessage
  contextKey: string
  isStreaming?: boolean
  reasoningEnabled: boolean
}) => {
  const showReasoning =
    reasoningEnabled &&
    message.role === MessageRole.Assistant &&
    Boolean((message as AgentMessage & { reasoning?: string }).reasoning)

  const hasAssistantContent =
    message.role === MessageRole.Assistant && typeof message.content === 'string' && message.content.trim().length > 0

  const showTyping = Boolean(isStreaming && !showReasoning && !hasAssistantContent)
  const showBubble = Boolean(
    (typeof message.content === 'string' && message.content.trim().length > 0) || (message.attachments?.length ?? 0) > 0
  )
  const dedupedToolCalls = dedupeToolCalls(message.toolCalls ?? [])

  return (
    <>
      {showReasoning && <ReasoningPanel reasoning={message.reasoning!} isStreaming={isStreaming} />}
      {showTyping && <MessageTypingAnimation />}
      {showBubble && <MessageBubble message={message} />}
      {!isStreaming && dedupedToolCalls.length > 0 && (
        <div className='mp-agent-tool-calls'>
          {dedupedToolCalls.map((call) => (
            <ToolCallCard
              key={call.id}
              call={call}
              contextKey={contextKey}
              messageId={message.id}
              chatId={message.chat}
            />
          ))}
        </div>
      )}
    </>
  )
}

const MessageGroup = ({
  message,
  contextKey,
  isStreaming,
  showParticipantChrome = false
}: {
  message: AgentMessage
  contextKey: string
  isStreaming?: boolean
  showParticipantChrome?: boolean
}) => {
  const runtime = useAgentRuntime()
  const reasoningEnabled = runtime.config.features?.reasoning !== false

  // Participant chrome (avatar + name/time) lives on the group, not on the
  // bubble, so reasoning panels, text bubbles, and tool calls all share the
  // same indent under the header.
  const showChrome = showParticipantChrome && Boolean(message.sender || message.agentName)
  const isUser = message.role === MessageRole.User

  // Ordered-parts render. Only assistant messages carry parts; absence (all
  // pre-existing messages) falls through to the legacy fixed-order layout.
  const body: ReactNode =
    message.role === MessageRole.Assistant && (message.parts?.length ?? 0) > 0 ? (
      <OrderedMessageParts
        message={message}
        contextKey={contextKey}
        isStreaming={isStreaming}
        reasoningEnabled={reasoningEnabled}
      />
    ) : (
      <LegacyMessageParts
        message={message}
        contextKey={contextKey}
        isStreaming={isStreaming}
        reasoningEnabled={reasoningEnabled}
      />
    )

  const groupClassName = [
    'mp-agent-message-group',
    showChrome ? 'mp-agent-message-group-with-chrome' : '',
    isUser ? 'mp-agent-message-group-user' : ''
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={groupClassName}>
      {showChrome && (
        <div className='mp-agent-message-group-header'>
          <MessageAvatar message={message} />
          <MessageHeader message={message} />
        </div>
      )}
      <div className='mp-agent-message-group-body'>{body}</div>
    </div>
  )
}

export default memo(MessageGroup)
